import React, { useEffect, useMemo, useRef, useState } from "react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  updateGuidanceRhythmCadence,
  updateGuidanceRhythmProfile,
} from "../../Store/Reducers/GuidanceSlice";
import { CADENCE_GENERIC_LABEL } from "../../Utils/Guidance/guidanceCadence";
import { buildRhythmRoleView } from "../../Utils/Guidance/guidanceRhythm";
import {
  GUIDANCE_ROLES,
  GuidanceRole,
} from "../../Utils/Guidance/guidanceRole";
import { toast } from "../../Utils/Helper/toast";
import CadenceMapModal from "../GuidancePlaybooks/CadenceMapModal";
import {
  CADENCE_ORDER,
  CadenceKey,
  resolveCadenceLabel,
} from "../GuidancePlaybooks/constants";

interface DraftCadence {
  time: string;
  bullets: string;
}

interface DraftRhythm {
  roleLabel: string;
  roleTagline: string;
  closingHeadline: string;
  closingBullets: string;
  cadences: Record<CadenceKey, DraftCadence>;
}

const emptyCadenceDraft = (): DraftCadence => ({ time: "", bullets: "" });

const emptyDraft = (): DraftRhythm => ({
  roleLabel: "",
  roleTagline: "",
  closingHeadline: "",
  closingBullets: "",
  cadences: {
    daily: emptyCadenceDraft(),
    weekly: emptyCadenceDraft(),
    biweekly: emptyCadenceDraft(),
    monthly: emptyCadenceDraft(),
    quarterly: emptyCadenceDraft(),
  },
});

const fieldClass = (isEditing: boolean) =>
  `w-full text-sm font-normal normal-case tracking-normal text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 ${
    !isEditing ? "bg-gray-50 text-gray-500 cursor-default" : "bg-white"
  }`;

/**
 * Admin > Rhythm & Outcomes — edit role chrome + cadence card bodies.
 * Display names come from Playbook Content User tags (cadence_tag), not here.
 * Preview opens CadenceMapModal; Publish writes profile + all five cadences.
 */
const RhythmContentTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { rhythmProfiles, rhythmCadences, playbookLinks, savingKey } =
    useAppSelector(state => state.guidance);

  const [role, setRole] = useState<GuidanceRole>("ae");
  const [draft, setDraft] = useState<DraftRhythm>(emptyDraft());
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [published, setPublished] = useState(false);
  const draftBeforeEdit = useRef<DraftRhythm>(emptyDraft());

  const profile = rhythmProfiles.find(p => p.role === role);
  const profileUpdated = profile?.updated_at;
  const cadenceStamp = rhythmCadences
    .filter(c => c.role === role)
    .map(c => c.updated_at)
    .join("|");

  const buildDraftFromLive = (): DraftRhythm => {
    const next = emptyDraft();
    next.roleLabel = profile?.role_label || "";
    next.roleTagline = profile?.role_tagline || "";
    next.closingHeadline = profile?.closing_headline || "";
    next.closingBullets = (profile?.closing_bullets || []).join("\n");
    for (const key of CADENCE_ORDER) {
      const row = rhythmCadences.find(
        c => c.role === role && c.cadence === key
      );
      next.cadences[key] = {
        time: row?.time || "",
        bullets: (row?.bullets || []).join("\n"),
      };
    }
    return next;
  };

  useEffect(() => {
    setDraft(buildDraftFromLive());
    setIsEditing(false);
    setHasUnpublishedChanges(false);
    setPublished(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, profileUpdated, cadenceStamp]);

  const handleStartEdit = () => {
    draftBeforeEdit.current = draft;
    setIsEditing(true);
  };

  const promptClickEdit = () => {
    if (!isEditing) {
      toast.info("Click the Edit button to make changes.");
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    setHasUnpublishedChanges(true);
  };

  const handleCancel = () => {
    setDraft(draftBeforeEdit.current);
    setIsEditing(false);
  };

  const isPublishing =
    savingKey === `rhythm-profile:${role}` ||
    !!savingKey?.startsWith(`rhythm:${role}:`);

  const handlePublish = async () => {
    const closingBullets = draft.closingBullets
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean);

    try {
      await dispatch(
        updateGuidanceRhythmProfile({
          role,
          role_label: draft.roleLabel.trim(),
          role_tagline: draft.roleTagline.trim() || null,
          closing_headline: draft.closingHeadline.trim() || null,
          closing_bullets: closingBullets,
        })
      ).unwrap();

      for (const key of CADENCE_ORDER) {
        const cell = draft.cadences[key];
        await dispatch(
          updateGuidanceRhythmCadence({
            role,
            cadence: key,
            time: cell.time.trim() || null,
            bullets: cell.bullets
              .split("\n")
              .map(s => s.trim())
              .filter(Boolean),
          })
        ).unwrap();
      }

      setShowPublishConfirm(false);
      setHasUnpublishedChanges(false);
      setPublished(true);
      setTimeout(() => setPublished(false), 1500);
    } catch {
      // Errors surface via rhythmError; keep confirm open for retry.
    }
  };

  const updateCadence = (
    key: CadenceKey,
    field: keyof DraftCadence,
    value: string
  ) => {
    setDraft({
      ...draft,
      cadences: {
        ...draft.cadences,
        [key]: {
          ...draft.cadences[key],
          [field]: value,
        },
      },
    });
  };

  const previewView = useMemo(() => {
    const live = buildRhythmRoleView(
      role,
      rhythmProfiles,
      rhythmCadences,
      playbookLinks
    );
    return {
      ...live,
      personaLabel: draft.roleLabel.trim() || live.personaLabel,
      personaTagline: draft.roleTagline.trim() || live.personaTagline,
      closingHeadline: draft.closingHeadline.trim() || live.closingHeadline,
      closingBullets: draft.closingBullets
        .split("\n")
        .map(s => s.trim())
        .filter(Boolean),
      cadences: CADENCE_ORDER.map(key => {
        const playbook = playbookLinks.find(
          p => p.role === role && p.cadence === key
        );
        const cell = draft.cadences[key];
        return {
          key,
          label: resolveCadenceLabel(playbook?.cadence_tag, key),
          time:
            cell.time.trim() ||
            live.cadences.find(c => c.key === key)?.time ||
            "",
          bullets: cell.bullets
            .split("\n")
            .map(s => s.trim())
            .filter(Boolean),
        };
      }),
    };
  }, [role, draft, rhythmProfiles, rhythmCadences, playbookLinks]);

  const roleMeta = GUIDANCE_ROLES.find(r => r.key === role);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Rhythm &amp; Outcomes Editor
        </h3>
        <p className="mt-1 text-sm text-gray-500 max-w-2xl">
          Cadence display names come from Playbook Content → User tag. This tab
          edits time, bullets, and role chrome only.
        </p>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50/80">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              Role
              <select
                value={role}
                onChange={e => setRole(e.target.value as GuidanceRole)}
                className="min-w-[140px] text-sm font-normal normal-case tracking-normal text-gray-900 border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300"
              >
                {GUIDANCE_ROLES.map(r => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            {!isEditing ? (
              <button
                type="button"
                onClick={handleStartEdit}
                className="text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] rounded-lg px-4 py-2 transition-colors"
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  className="text-sm font-semibold text-white bg-[#974ae4] hover:bg-[#8538d4] rounded-lg px-4 py-2 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors"
            >
              Preview
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {!hasUnpublishedChanges && !published && (
              <span className="text-xs text-gray-400">
                Make and save edits to enable Publish
              </span>
            )}
            {hasUnpublishedChanges && (
              <span className="text-xs font-semibold text-amber-700">
                Saved locally — not yet live
              </span>
            )}
            {published && (
              <span className="text-xs font-semibold text-green-700">
                Live for this org
              </span>
            )}
            <Button
              variant="primary"
              onClick={() => setShowPublishConfirm(true)}
              disabled={!hasUnpublishedChanges}
              loading={isPublishing}
            >
              {published ? "✓ Published" : "Publish"}
            </Button>
          </div>
        </div>

        <div className="px-5 py-5 space-y-4 max-w-3xl">
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            Role label
            <input
              type="text"
              value={draft.roleLabel}
              readOnly={!isEditing}
              onMouseDown={promptClickEdit}
              onChange={e => setDraft({ ...draft, roleLabel: e.target.value })}
              className={fieldClass(isEditing)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            Role tagline
            <input
              type="text"
              value={draft.roleTagline}
              readOnly={!isEditing}
              onMouseDown={promptClickEdit}
              onChange={e =>
                setDraft({ ...draft, roleTagline: e.target.value })
              }
              className={fieldClass(isEditing)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            Closing headline
            <input
              type="text"
              value={draft.closingHeadline}
              readOnly={!isEditing}
              onMouseDown={promptClickEdit}
              onChange={e =>
                setDraft({ ...draft, closingHeadline: e.target.value })
              }
              className={fieldClass(isEditing)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            Closing bullets{" "}
            <span className="font-normal normal-case tracking-normal text-gray-400">
              (one per line)
            </span>
            <textarea
              rows={4}
              value={draft.closingBullets}
              readOnly={!isEditing}
              onMouseDown={promptClickEdit}
              onChange={e =>
                setDraft({ ...draft, closingBullets: e.target.value })
              }
              className={`${fieldClass(isEditing)} resize-y`}
            />
          </label>
        </div>

        <div className="px-5 pb-5 space-y-3">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Cadence cards
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {CADENCE_ORDER.map(key => {
              const playbook = playbookLinks.find(
                p => p.role === role && p.cadence === key
              );
              const displayLabel = resolveCadenceLabel(
                playbook?.cadence_tag,
                key
              );
              return (
                <div
                  key={key}
                  className="rounded-xl border border-gray-200 bg-gray-50/40 p-4 space-y-3"
                >
                  <div className="text-sm font-semibold text-gray-900">
                    {displayLabel}{" "}
                    <span className="font-normal text-gray-400 text-xs">
                      ({CADENCE_GENERIC_LABEL[key]})
                    </span>
                  </div>
                  <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Time
                    <input
                      type="text"
                      value={draft.cadences[key].time}
                      readOnly={!isEditing}
                      onMouseDown={promptClickEdit}
                      onChange={e => updateCadence(key, "time", e.target.value)}
                      className={fieldClass(isEditing)}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                    Bullets{" "}
                    <span className="font-normal normal-case tracking-normal text-gray-400">
                      (one per line)
                    </span>
                    <textarea
                      rows={3}
                      value={draft.cadences[key].bullets}
                      readOnly={!isEditing}
                      onMouseDown={promptClickEdit}
                      onChange={e =>
                        updateCadence(key, "bullets", e.target.value)
                      }
                      className={`${fieldClass(isEditing)} resize-y`}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <CadenceMapModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        personaLabel={previewView.personaLabel}
        personaTagline={previewView.personaTagline}
        personaIcon=""
        closingHeadline={previewView.closingHeadline}
        closingBullets={previewView.closingBullets}
        cadences={previewView.cadences}
        onSelectCadence={() => setPreviewOpen(false)}
      />

      <CommonModal
        isOpen={showPublishConfirm}
        toggle={() => setShowPublishConfirm(false)}
        title="Publish rhythm changes?"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="gray" onClick={() => setShowPublishConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handlePublish}
              loading={isPublishing}
            >
              Publish
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600 leading-relaxed">
          This publishes the {roleMeta?.label || role} rhythm profile and all
          five cadence cards to the Hub.
        </p>
      </CommonModal>
    </div>
  );
};

export default RhythmContentTab;
